import React, { ReactNode, useState } from 'react';
import styled from '@emotion/styled';
import PaymentStatus from '~/Components/PaymentStatus';

import { CompleteBongQuery_venue_company_offers as Offer } from '~Scenes/POS/Queries/__queries__';

import NumberInput from '~ComponentsImproved/Numpad';

import { useSelector, useI18n } from '~/Hooks';
import { DISCOUNT_TYPE } from '~../__queries__';
import Button from '~ComponentsImproved/Button';
import CartTotals from './CartTotals';
import Discounts from './Discounts';
import { useCart } from '~Components/CartProvider';

const PaymentOuterWrapper = styled.div`
  background-color: var(--colorBlueGray1300);
  border-top: 1px solid var(--colorBlueGray1200);
  position: relative;
  z-index: 1;

  &:before {
    content: '';
    position: absolute;
    width: 100%;
    height: 32px;
    top: -32px;
    background: linear-gradient(180deg, rgba(17, 14, 22, 0) 0%, var(--colorBlueGray1300) 100%);
    z-index: -1;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-flow: row wrap;
  padding: 16px 12px 8px;
  justify-content: space-between;

  button {
    margin-bottom: 8px;
  }
`;

const PaymentWrapper = styled.div`
  align-self: flex-end;
  width: 100%;
  transition: all 0.2s;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
`;

interface Props {
  venueId: string;
  offers: Offer[];
  buttons?: ReactNode;
  paymentId: string | null;
  showMessageModal: boolean;
  setShowMessageModal: (val: boolean) => void;
  selectedHasVariations: boolean;
  showModifyModal: () => void;
  isTsr?: boolean;
}

const BottomCartSection = ({
  offers,
  buttons,
  paymentId,
  setShowMessageModal,
  selectedHasVariations,
  showModifyModal,
  isTsr,
}: Props) => {
  const cart = useCart();

  /**
   * This is set when we are applying a discount to
   * specific items in the cart.
   */
  const [isDiscountingItems, setIsDiscountingItems] = useState(false);

  const [manualDiscountType, setManualDiscountType] = useState<DISCOUNT_TYPE | null>(null);
  const { i18n } = useI18n();

  const splitOrder = useSelector((state) => state.POSSplitOrder);
  const payment = useSelector((state) => state.POSPayments.find((p) => p.id === paymentId));

  return (
    <PaymentOuterWrapper>
      <CartTotals />
      <PaymentWrapper>
        {payment && !splitOrder ? (
          // If we have an active payment, and we are not splitting, show payment status
          <PaymentStatus status={payment.status} paymentId={payment.id} />
        ) : cart.selectedItems.ids.length ? (
          // If we have selected items in cart, show options
          <Buttons>
            {cart.selectedItems.ids.length === 1 && selectedHasVariations && (
              <Button
                text={i18n('POS.Change').toUpperCase()}
                style={{ color: 'var(--colorUIBlue)' }}
                onTouchEnd={showModifyModal}
              />
            )}
            <Button
              text={i18n('POS.Message').toUpperCase()}
              onTouchEnd={() => setShowMessageModal(true)}
            />
            {!isTsr && <Discounts offers={offers} forSelectedItems />}

            <Button
              text={i18n('POS.Duplicate').toUpperCase()}
              onTouchEnd={() => {
                cart.selectedItems.duplicate();
                cart.selectedItems.clear();
              }}
            />
            <Button
              text={i18n('POS.Remove').toUpperCase()}
              onTouchEnd={cart.selectedItems.remove}
            />
            <Button
              text={i18n('POS.Cancel').toUpperCase()}
              onTouchEnd={cart.selectedItems.clear}
              height={'72px'}
              style={{ color: 'var(--colorUIRed)' }}
            />
          </Buttons>
        ) : (
          <>{buttons}</>
        )}
      </PaymentWrapper>

      {/* Used for individual selected items */}
      {manualDiscountType && (
        <NumberInput
          onSubmit={(n) => {
            setManualDiscountType(null);
            setIsDiscountingItems(false);

            const discount = {
              type: manualDiscountType,
              value: manualDiscountType == 'PRICE' ? Math.round(+n * 100) : parseInt(n),
            };

            if (isDiscountingItems) {
              cart.selectedItems.applyDiscount(discount);
            } else {
              cart.setAppliedDiscount(discount);
            }
          }}
          onClose={() => {
            setManualDiscountType(null);
          }}
        />
      )}
    </PaymentOuterWrapper>
  );
};

export default React.memo(BottomCartSection);
